// M1 throwaway: shared analytics chat session state machine.
//
// Both useLiveAnalyticsChat and useCaseAnalyticsChat delegate to this hook.
// Domain-specific concerns (system prompt, tools, session key derivation)
// live in the caller; this hook owns the lifecycle.

import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import {
  AWAITING_SESSION_TIMEOUT_MS,
  copilotSessionKeys,
  TERMINAL_STATUSES,
  useCopilotInvoke,
  useCopilotMessages,
} from "src/api/agents/copilot";
import { useCurrentWorkspace } from "src/api/queries";
import {
  Environment,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import type { Environment as WorkspaceEnvironment } from "src/hooks/useEnvironment";

type PersistedSession = { agentId: string; sessionId: string };
type SessionMap = Record<string, PersistedSession>;

type TransientChatState =
  | { kind: "idle" }
  | { kind: "provisioning"; pendingMessage: string | null }
  | { kind: "error"; message: string };

export type AnalyticsChatResult = {
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  isReady: boolean;
  isExpired: boolean;
  failureMessage: string | null;
  isRestoringSession: boolean;
  send: (message: string) => void;
  resetSession: () => void;
};

export const useAnalyticsChatSession = ({
  storageKey,
  sessionSlotKey,
  environment,
  provision,
  provisionReady = true,
  sessionExpiryThresholdMs,
  sessionExpiredMessage,
}: {
  storageKey: string;
  sessionSlotKey: string | null;
  environment: WorkspaceEnvironment;
  provision: (baseUrl: string) => Promise<string>;
  /** Gate eager provisioning until domain-specific data is ready. */
  provisionReady?: boolean;
  /** FE-side session expiry threshold in ms. Omit to disable expiry. */
  sessionExpiryThresholdMs?: number;
  /** Message shown when the session is expired. */
  sessionExpiredMessage?: string;
}): AnalyticsChatResult => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;
  const queryClient = useQueryClient();

  const [sessions, _setSessions] = useSessionStorage<SessionMap>(
    storageKey,
    {},
  );

  const writeSession = useCallback(
    (id: string, session: PersistedSession | null) => {
      _setSessions((prev) => {
        if (session === null) {
          return Object.fromEntries(
            Object.entries(prev).filter(([k]) => k !== id),
          );
        }
        return { ...prev, [id]: session };
      });
    },
    [_setSessions],
  );

  const persistedSession =
    sessionSlotKey !== null ? (sessions[sessionSlotKey] ?? null) : null;

  const [transient, setTransient] = useState<TransientChatState>({
    kind: "idle",
  });

  const agentId = persistedSession?.agentId ?? null;
  const sessionId = persistedSession?.sessionId ?? null;

  // Track current sessionSlotKey so the .catch() in the eager-provision
  // effect can ignore failures from a provision started for a stale slot.
  const sessionSlotKeyRef = useRef(sessionSlotKey);
  sessionSlotKeyRef.current = sessionSlotKey;

  // Side-effects (session write + error handling) live in the .then()/.catch()
  // of mutateAsync — not in onSuccess/onError — so in-flight provisions survive
  // provisionMutation.reset() when the user switches context.
  const provisionMutation = useMutation({
    mutationFn: (baseUrl: string) => provision(baseUrl),
  });

  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Stops StrictMode's double-invoked effect from provisioning two agents.
  const eagerProvisionStartedRef = useRef(false);

  const messagesQueryKey = copilotSessionKeys.messages(sessionId);

  const invokeMutation = useCopilotInvoke({
    agentId: agentId ?? undefined,
    agentVersionId: "latest",
    sessionId,
    source: "analytics",
    environment:
      environment === "live" ? Environment.PRODUCTION : Environment.SANDBOX,
    messagesQueryKey,
    onInvokeStarted: () => {
      awaitingSessionRef.current = true;
      clearTimeout(awaitingTimeoutRef.current);
      awaitingTimeoutRef.current = setTimeout(() => {
        awaitingSessionRef.current = false;
      }, AWAITING_SESSION_TIMEOUT_MS);
    },
  });

  const clearTransientState = useCallback(() => {
    setTransient({ kind: "idle" });
    awaitingSessionRef.current = false;
    clearTimeout(awaitingTimeoutRef.current);
    eagerProvisionStartedRef.current = false;
    invokeMutation.reset();
    provisionMutation.reset();
  }, [invokeMutation.reset, provisionMutation.reset]);

  // Eager-provision on mount so the first message doesn't pay latency.
  // The `provisionReady` gate lets callers delay until domain-specific data
  // (e.g. flow version names) is loaded. Functional `setTransient` preserves
  // a message the user sent before provision started.
  useEffect(() => {
    if (
      !baseUrl ||
      sessionSlotKey === null ||
      agentId !== null ||
      !provisionReady
    )
      return;
    if (eagerProvisionStartedRef.current) return;
    eagerProvisionStartedRef.current = true;
    setTransient((prev) =>
      prev.kind === "provisioning"
        ? prev
        : { kind: "provisioning", pendingMessage: null },
    );
    const slotKey = sessionSlotKey;
    provisionMutation
      .mutateAsync(baseUrl)
      .then((newAgentId) => {
        writeSession(slotKey, { agentId: newAgentId, sessionId: uuidv4() });
      })
      .catch((err: unknown) => {
        // Ignore failures from a provision started for a slot the user has
        // already navigated away from.
        if (sessionSlotKeyRef.current !== slotKey) return;
        const message = err instanceof Error ? err.message : String(err);
        setTransient({
          kind: "error",
          message: `Provision failed: ${message}`,
        });
      });
  }, [
    baseUrl,
    sessionSlotKey,
    agentId,
    provisionReady,
    provisionMutation.mutateAsync,
  ]);

  // Reset transient state when the session identity changes.
  const [previousSlotKey, setPreviousSlotKey] = useState(sessionSlotKey);
  if (previousSlotKey !== sessionSlotKey) {
    setPreviousSlotKey(sessionSlotKey);
    clearTransientState();
  }

  // Once provisioning settles, fire the buffered first message, if any.
  useEffect(() => {
    if (
      transient.kind !== "provisioning" ||
      agentId === null ||
      sessionId === null
    )
      return;
    const { pendingMessage } = transient;
    setTransient({ kind: "idle" });
    if (pendingMessage !== null) {
      invokeMutation.mutateAsync(pendingMessage).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when the provision settles
  }, [transient, agentId, sessionId]);

  const messagesQuery = useCopilotMessages(sessionId, {
    awaitingSessionRef,
    isPending: invokeMutation.isPending,
  });

  const rawMessages: SessionMessagesResponseMessagesInner[] = (
    messagesQuery.data?.pages.flatMap((p) => p.messages ?? []) ?? []
  ).toReversed();

  const sessionStatus = messagesQuery.data?.pages[0]?.session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  const sessionCreatedAt = messagesQuery.data?.pages[0]?.session?.created_at;
  const isSessionExpired =
    sessionExpiryThresholdMs != null &&
    sessionCreatedAt != null &&
    Date.now() - new Date(sessionCreatedAt).getTime() >
      sessionExpiryThresholdMs;

  const send = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || !baseUrl || isSessionExpired) return;

      if (agentId !== null && sessionId !== null && transient.kind === "idle") {
        try {
          await invokeMutation.mutateAsync(trimmed);
        } catch {
          // onError handles UI
        }
        return;
      }

      // Don't pile onto a failed provision.
      if (transient.kind === "error") return;

      // Not ready yet — buffer the message for when provision settles.
      setTransient({ kind: "provisioning", pendingMessage: trimmed });
    },
    [
      baseUrl,
      agentId,
      sessionId,
      transient,
      invokeMutation.mutateAsync,
      isSessionExpired,
    ],
  );

  const resetSession = useCallback(() => {
    if (transient.kind === "provisioning") return;
    if (sessionSlotKey === null) return;
    clearTransientState();
    writeSession(sessionSlotKey, null);
    queryClient.removeQueries({ queryKey: messagesQueryKey });
  }, [
    clearTransientState,
    transient,
    sessionSlotKey,
    writeSession,
    queryClient,
    messagesQueryKey,
  ]);

  const isAwaitingFirstPoll =
    awaitingSessionRef.current && sessionStatus == null;

  // A user is waiting once a message is buffered onto an in-flight provision.
  // A bare eager provision must not make the UI look busy.
  const hasPendingMessage =
    transient.kind === "provisioning" && transient.pendingMessage !== null;

  const isActive =
    hasPendingMessage ||
    invokeMutation.isPending ||
    isAwaitingFirstPoll ||
    (sessionStatus != null && !isSessionTerminal);

  // A restored session's history loads on open with nothing "active" yet — show
  // a skeleton over the empty state. `!provisionMutation.isSuccess` excludes a
  // freshly-provisioned new chat: its empty session also fetches (and 404s to
  // empty), but should show the new-conversation prompts, not the skeleton.
  const isRestoringSession =
    sessionId !== null &&
    messagesQuery.isLoading &&
    !isActive &&
    !provisionMutation.isSuccess;

  const invokeErrorMessage = invokeMutation.isError
    ? invokeMutation.error.message
    : null;

  const sessionFailedMessage =
    sessionStatus === SessionStatus.FAILED
      ? messagesQuery.data?.pages[0]?.session?.termination_reason
        ? `Something went wrong: ${messagesQuery.data.pages[0].session.termination_reason}`
        : "Something went wrong"
      : null;

  const transientError = transient.kind === "error" ? transient.message : null;
  const expiredError = isSessionExpired
    ? (sessionExpiredMessage ?? null)
    : null;

  const failureMessage =
    transientError ??
    expiredError ??
    invokeErrorMessage ??
    sessionFailedMessage;

  return {
    messages: rawMessages,
    isActive,
    isProvisioning: provisionMutation.isPending && hasPendingMessage,
    isReady: baseUrl != null && sessionSlotKey != null,
    isExpired: isSessionExpired,
    failureMessage,
    isRestoringSession,
    send,
    resetSession,
  };
};
